// "Share with..." sheet — pick one or more accepted partners, optionally
// add a note, fire one share request per partner.
//
// Accepts either a single meal or a batch of meals. In batch mode (length >1)
// we loop and call shareMeal() for each source meal — recipient sees N
// individual inbox cards. The "true bundle" UX (one grouped inbox card with
// a single accept/dismiss) requires a schema change and lives behind a
// future bundle_id column; this MVP intentionally skips that to ship today.

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Check, Loader2, Send, X } from "lucide-react";
import { loadConnections, type Connection } from "../lib/partners";
import { shareMeal } from "../lib/sharing";
import { friendlyError } from "../lib/errors";
import type { LoggedMeal } from "../lib/types";

interface Props {
  /** One or more meals to share. Single-item array preserves existing UX. */
  meals: LoggedMeal[];
  /** Optional override label shown in the header when sharing multiple meals
   *  (e.g. "Breakfast (7 items)"). Falls back to a generic summary. */
  bulkLabel?: string;
  onClose: () => void;
}

export default function ShareMealModal({ meals, bulkLabel, onClose }: Props) {
  const isBulk = meals.length > 1;

  const [partners, setPartners] = useState<Connection[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Bulk-mode progress so a 7-share loop doesn't look frozen.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const conns = await loadConnections();
        if (cancelled) return;
        setPartners(conns.filter((c) => c.status === "accepted" && c.partnerId));
      } catch (err) {
        if (!cancelled) setError(friendlyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle(partnerId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(partnerId)) next.delete(partnerId);
      else next.add(partnerId);
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    const recipients = Array.from(selected);
    try {
      if (!isBulk) {
        await shareMeal(meals[0].id, recipients, note);
      } else {
        // Bulk: serial loop so progress is meaningful + so we don't slam
        // Supabase with N parallel inserts. Annotate the note with a
        // simple "(X of Y from breakfast)" prefix so the recipient inbox
        // makes sense even though the cards are separate. The full bundle
        // UX comes later via bundle_id schema migration.
        const total = meals.length;
        setProgress({ done: 0, total });
        const bundleNote = note.trim();
        for (let i = 0; i < meals.length; i++) {
          const m = meals[i];
          const prefix = bulkLabel
            ? `[${i + 1}/${total} from ${bulkLabel}]`
            : `[${i + 1}/${total}]`;
          const finalNote = bundleNote ? `${prefix} ${bundleNote}` : prefix;
          await shareMeal(m.id, recipients, finalNote);
          setProgress({ done: i + 1, total });
        }
      }
      setSuccess(true);
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  // Header summary — single-meal UX preserved exactly; bulk gets a count.
  const headerSummary = useMemo(() => {
    if (isBulk) {
      const totalCals = meals.reduce((s, m) => s + m.estimate.totals.calories, 0);
      const label = bulkLabel ?? `${meals.length} items`;
      return { name: label, cals: Math.round(totalCals) };
    }
    const meal = meals[0];
    const name =
      meal.estimate.items.length === 0
        ? "this meal"
        : meal.estimate.items.length === 1
        ? meal.estimate.items[0].name
        : `${meal.estimate.items[0].name} + ${meal.estimate.items.length - 1} more`;
    return { name, cals: Math.round(meal.estimate.totals.calories) };
  }, [meals, bulkLabel, isBulk]);

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm" onClick={() => !busy && onClose()} />
      <div
        className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 border-t border-slate-800 rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="max-w-md md:max-w-2xl mx-auto p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-1 w-10 rounded-full bg-slate-700 mx-auto" />
            <button
              onClick={onClose}
              disabled={busy}
              className="absolute right-4 w-8 h-8 rounded-full text-slate-400 hover:bg-slate-800 flex items-center justify-center disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {success ? (
            <div className="py-10 text-center space-y-2">
              <Check className="w-10 h-10 text-emerald-400 mx-auto" strokeWidth={2.5} />
              <p className="text-sm text-emerald-400 font-semibold">
                {isBulk ? `Shared ${meals.length} items!` : "Shared!"}
              </p>
              <p className="text-xs text-slate-500">
                {selected.size === 1
                  ? "They'll see it in their Partners inbox."
                  : `${selected.size} partners will see it in their inbox.`}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-100">
                  {isBulk ? `Share ${bulkLabel ?? "meal"}` : "Share with…"}
                </h2>
                <p className="text-xs text-slate-400 mt-0.5 truncate">
                  {isBulk ? "Sharing " : "Sharing "}
                  <span className="text-slate-200">{headerSummary.name}</span> ·{" "}
                  {headerSummary.cals} kcal{isBulk ? " total" : ""}
                </p>
                {isBulk && (
                  <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                    Each item lands as its own card in their inbox — they
                    can accept or dismiss each one. (Grouped-inbox UX
                    coming in a future update.)
                  </p>
                )}
              </div>

              {loading ? (
                <div className="text-center text-slate-500 py-6 text-sm">Loading partners…</div>
              ) : partners.length === 0 ? (
                <div className="p-4 rounded-lg border border-dashed border-slate-700 text-center text-xs text-slate-400">
                  No connected partners yet. Invite someone in the Partners tab first.
                </div>
              ) : (
                <ul className="space-y-1.5 max-h-60 overflow-y-auto -mx-1 px-1">
                  {partners.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => toggle(p.partnerId!)}
                        className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-colors ${
                          selected.has(p.partnerId!)
                            ? "bg-emerald-950/40 border-emerald-700"
                            : "bg-slate-800/60 border-slate-700/60 hover:border-slate-600"
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-300 flex-shrink-0">
                          {p.partnerEmail.charAt(0).toUpperCase()}
                        </div>
                        <span className="flex-1 text-sm text-slate-100 truncate">{p.partnerEmail}</span>
                        {selected.has(p.partnerId!) && (
                          <Check className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  isBulk
                    ? "Optional note: 'this is what I had for breakfast'"
                    : "Optional note: 'tonight's dinner — we both had this'"
                }
                rows={2}
                maxLength={280}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-sm resize-none"
              />

              {error && (
                <div className="p-2.5 rounded-lg bg-red-950 border border-red-800 text-red-200 text-xs">
                  {error}
                </div>
              )}

              {busy && progress && (
                <div className="text-xs text-slate-400 tabular-nums">
                  Sharing… {progress.done} of {progress.total}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || selected.size === 0}
                  className="flex-[2] py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {busy
                    ? "Sharing…"
                    : selected.size === 0
                    ? "Pick partners"
                    : isBulk
                    ? `Share ${meals.length} items with ${selected.size}`
                    : `Share with ${selected.size}`}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
